import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import {useWardrobe} from '../context/WardrobeContext';
import {ChevronDownIcon, ShirtIcon} from './icons';

interface WardrobeAccessible {
    ownerId: string;
    ownerDisplayName?: string;
    permissions: string[];
    ownWardrobe: boolean;
}

export const WardrobeSwitcher: React.FC = () => {
    const navigate = useNavigate();
    const {selectedOwnerId, selectedOwnerDisplayName, setSelectedOwnerId, isOwnWardrobe} = useWardrobe();
    const [wardrobes, setWardrobes] = useState<WardrobeAccessible[]>([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const ref = useRef<HTMLDivElement>(null);

    const fetchWardrobes = useCallback(async () => {
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<WardrobeAccessible[]>('/wardrobe-access/accessible');
            setWardrobes(res.data || []);
        } catch (err) {
            console.error('Error fetching wardrobes:', err);
            setWardrobes([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchWardrobes();
    }, [fetchWardrobes]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formatLabel = (w: WardrobeAccessible) => {
        if (w.ownWardrobe) return 'Мой гардероб';
        return w.ownerDisplayName || `Гардероб ${w.ownerId.slice(0, 8)}...`;
    };

    const currentLabel = isOwnWardrobe
        ? 'Мой гардероб'
        : (selectedOwnerDisplayName ? `Гардероб: ${selectedOwnerDisplayName}` : 'Гардероб...');

    const handleSelect = (w: WardrobeAccessible) => {
        const id = w.ownWardrobe ? null : w.ownerId;
        const name = w.ownWardrobe ? null : (w.ownerDisplayName ?? null);
        setSelectedOwnerId(id, name, w.permissions ?? null);
        setOpen(false);
        navigate('/clothing');
    };

    if (loading || wardrobes.length <= 1) return null;

    return (
        <div className="relative" ref={ref} data-testid="wardrobe-switcher">
            <button
                onClick={() => setOpen(!open)}
                data-testid="wardrobe-switcher-toggle"
                aria-expanded={open}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 truncate max-w-[180px]"
                title={currentLabel}
            >
                <ShirtIcon className="w-4 h-4 flex-shrink-0"/>
                <span className="truncate">{currentLabel}</span>
                <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}/>
            </button>
            {open && (
                <div data-testid="wardrobe-switcher-menu"
                     className="fw-popover origin-top-right absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 min-w-[200px]">
                    {wardrobes.map((w) => {
                        const isSelected = (w.ownWardrobe && !selectedOwnerId) || (!w.ownWardrobe && selectedOwnerId === w.ownerId);
                        return (
                            <button
                                key={w.ownerId}
                                data-testid={`wardrobe-switcher-option-${w.ownWardrobe ? 'own' : w.ownerId}`}
                                aria-current={isSelected ? 'true' : undefined}
                                onClick={() => handleSelect(w)}
                                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg ${
                                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : ''
                                }`}
                            >
                                <ShirtIcon className="w-4 h-4 flex-shrink-0"/>
                                <span className="truncate">{formatLabel(w)}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
